import { useEffect, useMemo, useRef, useState } from "react";
import { useT } from "../i18n";
import { Knopf, Marke, euro } from "./index";
import { ARTWORT, Karten, Katalogwahl, alsZahl, einheitAusGroesse } from "./Positionen";
import { useLeistungsarten, useWaren, useZuschlaege } from "../daten/haken";
import { useTelefon } from "../schale/geraet";
import { merken, type Merkart } from "../daten/merkliste";
import { betragFehler } from "../daten/zahlfeld";
import { Betragssatz } from "./Betragssatz";

/* ══════════════════════════════════════════════════════════════════
   Eine Zeile dazu — Leistung, Ware, Zuschlag, Freitext (18.09.2026).

   **Das war der einzige echte Verlust.** Die Sammlung trug bis heute nur
   Mietposten; die alte Schnellerfassung konnte vier Arten mehr. Ein
   Anruf, bei dem neben dem Bagger noch eine Mulchleistung und eine
   Palette Rüttelplatten-Verschleißteile dazukommen, ging nur über die
   alte Maske (`docs/38-vermietmodul.md`, „Was bis zum Umschalten noch
   fehlt", Punkt 1).

   ── Dieselben Bausteine wie im Angebot ─────────────────────────────

   `Karten` und `Katalogwahl` stehen in `bausteine/Positionen.tsx` und
   werden hier benutzt, nicht nachgebaut: Wer im Angebot „+ Leistung"
   gelernt hat, soll es hier wiederfinden. Genau so machte es die
   Schnellerfassung (`NeueZeile`) — diese Datei ist ihr Nachfolger und
   nicht ihre Kopie.

   ── Keine Miete in dieser Reihe ────────────────────────────────────

   Vier Arten stehen zur Wahl, nicht fünf. Eine **Miete** kommt aus der
   Trefferliste: Dort hat sie einen Tagesstreifen, eine Lage und einen
   Preis, der zum Zeitraum gehört. Ein Maschinentyp, den man hier aus
   einer Katalogliste wählt, käme ohne all das in die Sammlung — und der
   Weg daneben wäre der ärmere von zweien. Wer ein zweites Gerät will,
   tippt es oben ein.

   ── Der Betrag steht im Feld, nicht in der Antwort ─────────────────

   Leistung und Ware bringen ihren Satz aus dem Stamm mit; er steht als
   Platzhalter im Feld, und wer etwas anderes zusagt, tippt es hin
   (Regel 4 gilt erst bei der Annahme). Ein **Zuschlag** im Stamm ist
   eine Regel und kein Betrag — „5 % vom Netto" lässt sich nicht
   hinschreiben —, also steht dort nichts und die Zahl wird eingetippt.
   Genau dafür ist diese Zeile da.

   Klassen: `ml-dazu*`, gegen alle Stile geprüft.
   ══════════════════════════════════════════════════════════════════ */

/** Die vier Arten, die hier entstehen können. */
const ARTEN: Merkart[] = ["leistung", "verkauf", "zuschlag", "text"];

/** Der Schlüssel für „ohne Katalog" — eine Zeile, die ihren Namen selbst trägt. */
const FREI = "frei";

export function ZeileDazu() {
  const [art, setzeArt] = useState<Merkart | "">("");
  return (
    <div className="ml-dazu">
      {art ? (
        <Neuzeile
          key={art}
          art={art}
          fertig={() => setzeArt("")}
        />
      ) : null}
      <Karten
        erste={false}
        aktiv={art}
        schmal
        arten={ARTEN}
        waehlen={(gewaehlt) => setzeArt(art === gewaehlt ? "" : gewaehlt)}
      />
    </div>
  );
}

function Neuzeile({ art, fertig }: { art: Merkart; fertig: () => void }) {
  const t = useT();
  const { data: leistungsListe } = useLeistungsarten();
  const { data: warenListe } = useWaren();
  const { data: zuschlagsListe } = useZuschlaege();

  const telefon = useTelefon();

  const [wahl, setzeWahl] = useState("");
  const [bezeichnung, setzeBezeichnung] = useState("");
  const [menge, setzeMenge] = useState("");
  const [preisText, setzePreisText] = useState("");
  const mengeFeld = useRef<HTMLInputElement>(null);

  /** Die drei Kataloge in derselben Form: Kennung, Titel, Einheit, Satz. */
  const katalog = useMemo(() => {
    if (art === "leistung") {
      return (leistungsListe?.zeilen ?? []).filter((l) => l.aktiv).map((l) => ({
        id: l.id, titel: l.label, unten: einheitAusGroesse(l.groesse),
        einheit: einheitAusGroesse(l.groesse), netto: l.netto as number | null,
        aufmass: l.ortsaufnahme,
      }));
    }
    if (art === "verkauf") {
      return (warenListe?.zeilen ?? []).filter((w) => w.aktiv).map((w) => ({
        id: w.id, titel: w.label, unten: w.einheit,
        einheit: w.einheit, netto: w.netto as number | null,
        aufmass: false,
      }));
    }
    if (art === "zuschlag") {
      return (zuschlagsListe?.zeilen ?? []).filter((z) => z.aktiv).map((z) => ({
        id: z.code, titel: z.label, unten: z.code,
        einheit: "", netto: null as number | null,
        aufmass: false,
      }));
    }
    return [];
  }, [art, leistungsListe, warenListe, zuschlagsListe]);

  const satz = katalog.find((k) => k.id === wahl);

  /* **Am Schreibtisch springt der Fokus auf die Menge, am Telefon nicht**
     (18.09.2026). Wer aus dem Katalog gewählt hat, tippt mit der Maus als
     Nächstes die Menge — dort ist ein gesetzter Fokus ein gesparter Klick.
     Am Telefon holt derselbe Sprung die Zahlentastatur über die halbe
     Sammlung, und zwar für ein Feld, dessen Vorgabe 1 in den meisten Fällen
     stimmt. Dieselbe Regel wie bei den Blättern: nichts fokussieren, was
     niemand angetippt hat (40-mobiles-grundgeruest.md). */
  useEffect(() => {
    if (wahl && wahl !== FREI && !telefon) mengeFeld.current?.focus();
  }, [wahl, telefon]);

  const mengeZahl = alsZahl(menge);
  /* Eine dritte Stelle wird gesagt, nicht verschluckt (N4-06): Solange sie
     dasteht, legt Enter nichts an. */
  const preisFehler = betragFehler(preisText);
  const vonHand = preisFehler === null ? alsZahl(preisText) : undefined;
  const einzel = vonHand ?? satz?.netto ?? null;

  const bereit = art === "text"
    ? Boolean(bezeichnung.trim())
    : wahl === FREI ? Boolean(bezeichnung.trim()) : Boolean(satz);

  /* Die Schlüssel ausgeschrieben, nicht zusammengesetzt — sonst findet sie
     niemand, der nach einem verwaisten Satz sucht. */
  const worte = art === "leistung"
    ? { suche: "positionen.sucheLeistung", kein: "positionen.keineLeistung", leer: "positionen.leerLeistung", andere: "positionen.andereLeistung" }
    : art === "verkauf"
      ? { suche: "positionen.sucheWare", kein: "positionen.keineWare", leer: "positionen.leerWare", andere: "positionen.andereWare" }
      : { suche: "positionen.sucheZuschlag", kein: "positionen.keinZuschlag", leer: "positionen.leerZuschlag", andere: "positionen.andererZuschlag" };

  const anlegen = () => {
    if (!bereit || preisFehler !== null) return;
    if (art === "text") {
      merken({ art, bezeichnung: bezeichnung.trim(), menge: 0, netto: 0 });
      fertig();
      return;
    }
    /* **Die Kennung geht mit, wenn es eine gibt.** Aus ihr holt die andere
       Seite Bezeichnung, Einheit und Satz aus dem Stamm; ohne sie ist es
       eine Zeile von Hand, und dann trägt sie alles selbst. */
    const ausKatalog = wahl === FREI
      ? {}
      : art === "leistung" ? { leistungsartId: wahl }
        : art === "verkauf" ? { wareId: wahl }
          : { zuschlagCode: wahl };
    merken({
      art,
      bezeichnung: bezeichnung.trim() || satz?.titel || "",
      /* V-22: Eine Leistung mit Aufmaß ohne getippte Menge bleibt bei 0. */
      menge: mengeZahl ?? (satz?.aufmass ? 0 : 1),
      ...(satz?.aufmass ? { aufmass: true } : {}),
      netto: einzel ?? 0,
      ...(satz?.einheit ? { einheitKey: satz.einheit } : {}),
      ...ausKatalog,
    });
    fertig();
  };

  const beiTaste = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") { e.preventDefault(); anlegen(); }
    if (e.key === "Escape") { e.preventDefault(); fertig(); }
  };

  return (
    <div className="ml-neu">
      <span className="feld-schild">{t("positionen.dazuArt", { art: t(ARTWORT[art] ?? "") })}</span>

      {art !== "text" && !wahl ? (
        <Katalogwahl
          eintraege={katalog.map((k) => ({
            id: k.id,
            titel: k.titel,
            unten: k.unten ? (k.einheit === k.unten ? t(k.unten) : k.unten) : undefined,
            rand: k.netto === null ? undefined : (
              <span className="nr">
                {t("positionen.jeEinheit", { betrag: euro(k.netto), einheit: t(k.einheit) })}
              </span>
            ),
          }))}
          platz={t(worte.suche)}
          leerSatz={(wort) => (katalog.length ? t(worte.kein, { wort }) : t(worte.leer))}
          waehlen={(id) => {
            setzeWahl(id);
            setzeBezeichnung(katalog.find((k) => k.id === id)?.titel ?? "");
          }}
          abbrechen={fertig}
          fuss={(
            <button
              type="button"
              className="pos-neu-frei"
              onClick={() => { setzeWahl(FREI); setzeBezeichnung(""); }}
            >
              {t(worte.andere)}
            </button>
          )}
        />
      ) : (
        <>
          {/* **Fokus nur, wo noch nichts steht.** Eine Freitextzeile und
              „andere Leistung" leben davon, dass man sofort tippt — dort ist
              die Tastatur die Handlung und keine Störung. Nach einer Wahl aus
              dem Katalog steht der Name schon darin; am Telefon schöbe der
              Fokus dann eine Tastatur über die Sammlung, ohne dass jemand
              etwas zu schreiben hätte (18.09.2026). */}
          <input
            className="feld feld-zeile"
            value={bezeichnung}
            autoFocus={!telefon || !satz}
            placeholder={t(art === "text" ? "angebotBauen.platzText"
              : art === "leistung" ? "angebotBauen.platzLeistung"
                : art === "verkauf" ? "angebotBauen.platzVerkauf" : "positionen.platzZuschlag")}
            aria-label={t("angebotBauen.bezeichnung")}
            onChange={(e) => setzeBezeichnung(e.target.value)}
            onKeyDown={beiTaste}
          />

          {art === "text" ? null : (
            <div className="ml-neu-zahlen">
              <input
                ref={mengeFeld}
                className="feld feld-zahl"
                inputMode="decimal"
                value={menge}
                placeholder={String(1)}
                aria-label={t("angebotBauen.spalteMenge")}
                onChange={(e) => setzeMenge(e.target.value)}
                onKeyDown={beiTaste}
              />
              <input
                className="feld feld-zahl"
                inputMode="decimal"
                value={preisText}
                aria-invalid={preisFehler !== null}
                placeholder={einzel === null ? t("allgemein.strich") : euro(einzel)}
                aria-label={t("angebotBauen.spalteEinzel")}
                onChange={(e) => setzePreisText(e.target.value)}
                onKeyDown={beiTaste}
              />
            </div>
          )}
          {art === "text" ? null : <Betragssatz text={preisText} />}

          {/* Ein Satz, den niemand kennt, ist keine Sperre: Der Betrag wird
              am Telefon gesagt und gehört ins Feld. Die Marke sagt nur, dass
              hier nichts aus dem Stamm steht. */}
          {einzel === null && art !== "text" ? (
            <Marke art="stopp">{t("positionen.keinPreis")}</Marke>
          ) : null}
        </>
      )}

      <div className="ml-neu-knoepfe">
        <Knopf art="haupt" klein disabled={!bereit || preisFehler !== null} onClick={anlegen}>
          {t("positionen.hinzufuegen")}
        </Knopf>
        <Knopf art="stumm" klein onClick={fertig}>{t("allgemein.abbrechen")}</Knopf>
      </div>
    </div>
  );
}
